import { Form, Input, Radio, Select, Checkbox, Typography } from 'antd';
import { useStoreState } from '../../store';
import type { FormInstance } from 'antd/lib/form';
import { type FormProps } from 'antd/lib/form';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { useEffect } from 'react';
import type { PartnerPackagingV2 } from '../../store/services/PartnerService/types';

const { Paragraph } = Typography;

export interface PackagingFormProps {
  form: FormInstance;
  formValues?: any;
  activePartnerPackaging?: PartnerPackagingV2;
}

const useStyles = createUseStyles({
  checkboxLineThrough: {
    '& > label.ant-checkbox-wrapper-checked > span': {
      textDecoration: 'line-through',
    },
  },
});

export const PACKAGING_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
};

export const PackagingForm = ({
  form,
  formValues,
  activePartnerPackaging,
  ...props
}: PackagingFormProps & FormProps) => {
  const i18n = useStoreState((state) => state.i18n.activeResources);
  const classes = useStyles();

  const handleCancelClick = () => {
    form.resetFields();
  };

  useEffect(() => {
    form.setFieldsValue({
      packagingActive: activePartnerPackaging?.active ? PACKAGING_STATUS.ACTIVE : PACKAGING_STATUS.INACTIVE,
      name: activePartnerPackaging?.name,
      description: activePartnerPackaging?.description,
      length: activePartnerPackaging?.linearDimensions?.length,
      width: activePartnerPackaging?.linearDimensions?.width,
      height: activePartnerPackaging?.linearDimensions?.height,
      packagingMaterial: activePartnerPackaging?.packagingMaterial,
      partnerProvidedId: activePartnerPackaging?.partnerProvidedId,
      packagingSizeName: activePartnerPackaging?.packagingSizeName,
      packagingWeight: activePartnerPackaging?.packagingWeight?.weight,
      maxContentWeight: activePartnerPackaging?.maxContentWeight?.weight,
      linearUnit: activePartnerPackaging?.linearDimensions?.linearUnit || 'in',
      weightUnit: activePartnerPackaging?.packagingWeight?.weightUnit || 'lb',
      unsupportedProductDetails: activePartnerPackaging?.unsupportedProductDetails,
    });
  }, [activePartnerPackaging, form]);

  if (formValues) {
    form.setFieldsValue({
      name: formValues.name,
      description: formValues.description,
      length: formValues.length,
      width: formValues.width,
      height: formValues.height,
      partnerProvidedId: formValues.partnerProvidedId,
      packagingType: formValues.packagingType,
      packagingName: formValues.packagingName,
      packagingWeight: formValues.weight,
      maxContentWeight: formValues.weight,
      linearUnit: formValues.linearUnit || 'in',
      weightUnit: formValues.weightUnit || 'lb',
      productDetails: formValues.unsupportedProductDetails,
    });
  }

  return (
    <Form form={form} onReset={handleCancelClick} layout={'vertical'} {...props}>
      {activePartnerPackaging && (
        <Form.Item label={<b>Packaging ID</b>} style={{ marginBottom: 10 }} name="packagingId">
          <Paragraph copyable>{activePartnerPackaging?.packagingId}</Paragraph>
        </Form.Item>
      )}

      <Form.Item
        label={<b>Name</b>}
        style={{ marginBottom: 10 }}
        name="name"
        rules={[{ required: true, message: 'Name is required' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={<b>Description</b>}
        style={{ marginBottom: 10 }}
        name="description"
        rules={[{ required: false }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={<b>Packaging Size Name</b>}
        style={{ marginBottom: 10 }}
        name="packagingSizeName"
        rules={[{ required: true, message: 'Packaging Name is required' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={<b>Packaging ID Alias</b>}
        style={{ marginBottom: 10 }}
        name="partnerProvidedId"
        help="You may optionally define this ID, and it can be used in lieu of the Shipium generated Packaging ID."
      >
        <Input />
      </Form.Item>

      <Form.Item label={<b>Packaging Active</b>} style={{ marginBottom: 10 }} name="packagingActive">
        <Radio.Group>
          <Radio.Button value="active">Active</Radio.Button>
          <Radio.Button value="inactive">Inactive</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={<b>Packaging Material</b>}
        style={{ marginBottom: 10 }}
        name="packagingMaterial"
        rules={[{ required: true, message: 'Packaging Type is required' }]}
      >
        <Radio.Group>
          <Radio.Button value="box">Box</Radio.Button>
          <Radio.Button value="envelope">Envelope</Radio.Button>
          <Radio.Button value="flat_pack">Flat Pack</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        help={i18n['partner.packaging.helpReformatMessage']}
        label={<b>Linear Dimensions</b>}
        style={{ marginBottom: 0 }}
        wrapperCol={{ span: 14, offset: props?.wrapperCol?.offset || 0 }}
      >
        <Form.Item
          label={'Length'}
          name="length"
          rules={[{ required: true, message: 'required' }]}
          style={{ display: 'inline-block', width: 'calc(25% - 12px)', marginRight: '10px' }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={'Width'}
          name="width"
          rules={[{ required: true, message: 'required' }]}
          style={{ display: 'inline-block', width: 'calc(25% - 12px)', marginRight: '10px' }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={'Height'}
          name="height"
          rules={[{ required: true, message: 'required' }]}
          style={{ display: 'inline-block', width: 'calc(25% - 12px)', marginRight: '10px' }}
        >
          <Input />
        </Form.Item>
        <Form.Item label={' '} name="linearUnit" style={{ display: 'inline-block' }}>
          <Select>
            <Select.Option value={'in'}>in</Select.Option>
            <Select.Option value={'cm'}>cm</Select.Option>
          </Select>
        </Form.Item>
      </Form.Item>
      <Form.Item label={<b>Weight</b>} style={{ marginBottom: 10 }}>
        <Form.Item
          key={'packagingWeight'}
          name="packagingWeight"
          rules={[{ required: true, message: 'required' }]}
          label={'Packaging Weight'}
          style={{ display: 'inline-block', width: 'calc(40% - 12px)', marginRight: '10px' }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          key={'maxContentWeight'}
          name="maxContentWeight"
          rules={[{ required: true, message: 'required' }]}
          label={'Max Content Weight'}
          style={{ display: 'inline-block', width: 'calc(40% - 12px)', marginRight: '10px' }}
        >
          <Input />
        </Form.Item>
        <Form.Item key={'weightUnit'} label={' '} name="weightUnit" style={{ display: 'inline-block' }}>
          <Select>
            <Select.Option key={'lb'} value={'lb'}>
              lbs
            </Select.Option>
            <Select.Option key={'kgs'} value={'kg'}>
              kgs
            </Select.Option>
          </Select>
        </Form.Item>
      </Form.Item>
      <Form.Item
        name="unsupportedProductDetails"
        label={<b>Unsupported Products</b>}
        style={{ marginBottom: 0 }}
        wrapperCol={{ span: 23, offset: props?.wrapperCol?.offset || 0 }}
      >
        <Checkbox.Group
          style={{ width: '100%', marginBottom: '10px' }}
          className={classNames({ [classes.checkboxLineThrough]: true })}
        >
          <Checkbox key="ORMD" value="ORMD" style={{ lineHeight: '32px' }}>
            ORM-D
          </Checkbox>
          <Checkbox key="Li-ion" value="Li-ion" style={{ lineHeight: '32px' }}>
            Lithium Ion
          </Checkbox>
          <Checkbox key="Fragile" value="Fragile" style={{ lineHeight: '32px' }}>
            Fragile
          </Checkbox>
          <Checkbox key="LQ" value="LQ" style={{ lineHeight: '32px' }}>
            Limited Quantity
          </Checkbox>
        </Checkbox.Group>
      </Form.Item>
    </Form>
  );
};
